import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { fuelCSVImportService } from '../../../services/fuelCSVImportService';
import { ImportResult } from '../../../types/fuelImport';
import { ColumnMapping } from '../../../types/supplier';

/**
 * Page de traitement des données pour l'importation de carburant
 */
const FuelProcessPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { currentUser, currentOrganization } = useAuth();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [importId, setImportId] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  
  // Étapes de l'importation
  const steps = [
    'Sélection du fichier',
    'Configuration du mapping',
    'Importation des données',
    'Résultats'
  ];
  
  // Charger les données du localStorage au chargement de la page
  useEffect(() => {
    const storedHeaders = localStorage.getItem('fuelImport_headers');
    const storedData = localStorage.getItem('fuelImport_data');
    const storedMapping = localStorage.getItem('fuelImport_mapping');
    const storedDateFormat = localStorage.getItem('fuelImport_dateFormat');
    const storedSaveConfig = localStorage.getItem('fuelImport_saveConfig');
    const storedNewSupplierName = localStorage.getItem('fuelImport_newSupplierName');
    const storedSupplierId = localStorage.getItem('fuelImport_supplierId');
    
    if (!storedHeaders || !storedData || !storedMapping || !storedDateFormat) {
      // Rediriger vers la page d'importation si les données ne sont pas disponibles
      navigate('/admin/fuel/import');
      return;
    }
    
    // Vérifier que l'utilisateur est connecté et a une organisation
    if (!currentOrganization || !currentUser) {
      setError('Vous devez être connecté pour effectuer cette action.');
      setLoading(false);
      return;
    }
    
    // Démarrer le traitement des données
    startProcessing(
      JSON.parse(storedData),
      JSON.parse(storedMapping),
      storedDateFormat,
      storedSaveConfig === 'true',
      storedNewSupplierName || undefined,
      storedSupplierId || undefined
    );
  }, [navigate, currentUser, currentOrganization]);
  
  // Démarrer le traitement des données
  const startProcessing = async (
    data: Record<string, any>[],
    mapping: ColumnMapping,
    dateFormat: string,
    saveConfig: boolean,
    newSupplierName?: string,
    supplierId?: string
  ) => {
    setLoading(false);
    setProcessing(true);
    setProgress(0);
    
    try {
      // Convertir les données brutes en transactions de carburant
      const transactions = fuelCSVImportService.convertToFuelTransactions(data, mapping);
      
      // Simuler une progression
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);
      
      // Traiter les transactions
      const importResult = await fuelCSVImportService.processFuelTransactions(
        transactions,
        currentOrganization.id,
        currentUser.id
      );
      
      // Arrêter la simulation de progression
      clearInterval(progressInterval);
      setProgress(100);
      
      // Enregistrer le résultat
      setResult(importResult);
      
      // Stocker le résultat dans le localStorage pour la page de résultats
      localStorage.setItem('fuelImport_result', JSON.stringify(importResult));
      
      // Rediriger vers la page de résultats après un court délai
      setTimeout(() => {
        navigate('/admin/fuel/import/results');
      }, 1000);
    } catch (error) {
      console.error('Erreur lors du traitement des données:', error);
      setError('Une erreur est survenue lors du traitement des données.');
      setProcessing(false);
    }
  };
  
  // Gérer le clic sur le bouton "Retour"
  const handleBack = () => {
    navigate('/admin/fuel/import/mapping');
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Importation des données
        </Typography>
        
        <Stepper activeStep={2} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ p: 3, mb: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6">
                Chargement des données...
              </Typography>
            </Box>
          ) : processing ? (
            <Box sx={{ py: 4 }}>
              <Typography variant="h6" gutterBottom>
                Traitement des données en cours...
              </Typography>
              
              <Box sx={{ width: '100%', mt: 2, mb: 4 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                  {progress}%
                </Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                Veuillez patienter pendant que nous traitons vos données. Cette opération peut prendre quelques instants.
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Ne fermez pas cette page pendant le traitement.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 4 }}>
              <Typography variant="h6" gutterBottom>
                Traitement terminé
              </Typography>
              
              <Typography variant="body1" paragraph>
                Le traitement des données est terminé. Vous allez être redirigé vers la page des résultats.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={30} />
              </Box>
            </Box>
          )}
        </Paper>
        
        {!processing && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={processing}
            >
              Retour
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default FuelProcessPage;